import {
  Commission,
  CommissionProfile,
  isCm2PriceType,
} from "../../../core/models/entities/Commission";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Method } from "../../../core/models/entities/TransferOptions";

/**
 * Exact frontend replica of the API's PriceAdjustmentService so live previews
 * show the same numbers the customer will see.
 *
 * API rules (KitMaker.Services/Application/Common/PriceAdjustment):
 * - adjusted = amount * (1 + pct/100) for "%", amount + value for flat.
 * - Precedence is first-match-wins: Method > Type > OverAll.
 * - A method commission matches when MethodKey.split('_')[0] equals the
 *   logo's TransferMethod.Key AND its size bucket fits the logo:
 *   - STANDARD price type: MaxDimensions "HxW" (mm), H >= heightMm and
 *     W >= widthMm.
 *   - CM2 price types: MaxSizeCM2 >= (heightMm/10) * (widthMm/10).
 * - Among fitting buckets the SMALLEST wins (OrderBy MaxSizeCM2, then the
 *   padded MaxDimensions ordering).
 * - Extra fees use ONLY the AdditionalFees rate and heating ONLY the Heating
 *   rate - neither falls back to the overall commission.
 *
 * In-memory cm2 size rows always carry the numeric bucket in maxSizeCM2
 * (mapCommissions normalizes loaded rows, including legacy defective rows
 * that only stored the "100 cm²" label in maxDimensions, and the editors set
 * it on new rows), so the CM2 branch here evaluates them exactly like the API
 * evaluates the saved shape (maxSizeCM2 + CM2 priceType, maxDimensions null).
 */

export type CommissionRuleSource = "method" | "type" | "default" | "none";

export interface AppliedCommissionRule {
  source: CommissionRuleSource;
  commission?: Commission;
}

/** API AddCommission: percentage multiplies, flat adds. */
export function applyCommissionAmount(
  amount: number,
  commission?: Commission
): number {
  if (!commission) return amount;
  return commission.unit === "%"
    ? amount * (1 + commission.value / 100)
    : amount + commission.value;
}

export function formatCommissionRate(
  commission: Commission | undefined,
  currency: string
): string {
  if (!commission) return "0%";
  return commission.unit === "%"
    ? `+${commission.value}%`
    : `+${commission.value} ${currency}`;
}

function parseMaxDimensions(
  maxDimensions: string | null
): { height: number; width: number } | undefined {
  if (!maxDimensions || !maxDimensions.includes("x")) return undefined;
  const splitIndex = maxDimensions.indexOf("x");
  const height = Number(maxDimensions.substring(0, splitIndex));
  const width = Number(maxDimensions.substring(splitIndex + 1));
  if (isNaN(height) || isNaN(width)) return undefined;
  return { height, width };
}

/** API MaxSizeIsBelowLogoSize, using the method's NAV price type. */
function sizeBucketFitsLogo(
  commission: Commission,
  logo: BaseLogo,
  method?: Method
): boolean {
  // The NAV price type arrives as a string ("Standard", "CM2", ...) even
  // though the TS type says enum; treat everything non-Standard as CM2.
  // Without method context, fall back to the rule's own shape/priceType.
  const priceType = method ? String(method.priceType) : undefined;
  const isCm2 = priceType
    ? priceType !== "Standard"
    : commission.maxSizeCM2 != null || isCm2PriceType(commission.priceType);

  if (isCm2) {
    // API: `commission.MaxSizeCM2 == default` (null or 0) never matches.
    if (!commission.maxSizeCM2) return false;
    const cm2 = (logo.heightMm / 10) * (logo.widthMm / 10);
    return commission.maxSizeCM2 >= cm2;
  }

  const dims = parseMaxDimensions(commission.maxDimensions);
  if (!dims) return false;
  return dims.height >= logo.heightMm && dims.width >= logo.widthMm;
}

/** Numeric-aware compare that mirrors the API's OrderByPadded ("9x9" < "10x10"). */
function comparePadded(a: string, b: string): number {
  const pad = (s: string) => s.replace(/\d+/g, (m) => m.padStart(10, "0"));
  return pad(a) < pad(b) ? -1 : pad(a) > pad(b) ? 1 : 0;
}

/**
 * Resolves which commission rule the API would apply to this logo's product
 * prices (first-match-wins Method > Type > OverAll).
 */
export function resolveCommissionRule(
  profile: CommissionProfile | undefined,
  logo: BaseLogo | null | undefined,
  transfers: Method[]
): AppliedCommissionRule {
  if (!profile || !logo) return { source: "none" };

  const logoMethodKey = logo.transferMethod?.key;
  const logoTypeKey = logo.transferMethod?.type;

  // Method: MethodKey.split('_')[0] must equal the logo's TransferMethod.Key
  // and the size bucket must fit; the smallest fitting bucket wins.
  if (logoMethodKey) {
    const candidates = (profile.methods ?? []).filter((c) => {
      if (!c.methodKey) return false;
      if (c.methodKey.split("_")[0] !== logoMethodKey) return false;
      const method = transfers.find((m) => m.methodKey === c.methodKey);
      return sizeBucketFitsLogo(c, logo, method);
    });
    if (candidates.length > 0) {
      const sorted = [...candidates].sort((a, b) => {
        // API: OrderBy(MaxSizeCM2) with nulls first, stable tie-break on the
        // padded MaxDimensions ordering.
        const aCm2 = a.maxSizeCM2;
        const bCm2 = b.maxSizeCM2;
        if ((aCm2 == null) !== (bCm2 == null)) return aCm2 == null ? -1 : 1;
        if (aCm2 != null && bCm2 != null && aCm2 !== bCm2) return aCm2 - bCm2;
        return comparePadded(a.maxDimensions ?? "", b.maxDimensions ?? "");
      });
      return { source: "method", commission: sorted[0] };
    }
  }

  // Type: exact TypeKey match.
  if (logoTypeKey) {
    const type = (profile.typeOnly ?? []).find(
      (c) => c.typeKey === logoTypeKey
    );
    if (type) return { source: "type", commission: type };
  }

  // OverAll fallback.
  if (profile.overAll) return { source: "default", commission: profile.overAll };

  return { source: "none" };
}

/** Extra fees use ONLY the AdditionalFees rate (no overall fallback). */
export function resolveAdditionalFeesRule(
  profile: CommissionProfile | undefined
): AppliedCommissionRule {
  if (profile?.additionalFees && profile.additionalFees.value !== 0) {
    return { source: "default", commission: profile.additionalFees };
  }
  return { source: "none" };
}

/** Heating uses ONLY the Heating rate (no overall fallback). */
export function resolveHeatingRule(
  profile: CommissionProfile | undefined
): AppliedCommissionRule {
  if (profile?.heating && profile.heating.value !== 0) {
    return { source: "default", commission: profile.heating };
  }
  return { source: "none" };
}

/** Human label for a resolved rule, e.g. "Method override (STANDARD ≤ 100x100)". */
export function describeCommissionRule(
  rule: AppliedCommissionRule,
  transfers: Method[]
): string {
  switch (rule.source) {
    case "method": {
      const method = transfers.find(
        (m) => m.methodKey === rule.commission?.methodKey
      );
      const name = method?.displayName ?? rule.commission?.methodKey ?? "";
      const bucket = rule.commission?.maxSizeCM2
        ? `${rule.commission.maxSizeCM2} cm²`
        : rule.commission?.maxDimensions ?? "";
      return bucket
        ? `Method override — ${name} (≤ ${bucket})`
        : `Method override — ${name}`;
    }
    case "type":
      return `Type rule — ${rule.commission?.typeKey ?? ""}`;
    case "default":
      return "Default commission";
    default:
      return "No commission applied";
  }
}
